import React, { useState, useEffect, useCallback } from 'react';
import { TasksApi, DefaultApi, PersonasApi } from '../BackendApiClient/apis';
import {
  InputTask,
  TaskType,
  TaskStatus,
  PersonaResponse,
  PersonaGroupResponse,
  RegionResponseFromJSON,
  TaskResponse,
  TaskExecutionResponse,
} from '../BackendApiClient/models';
import { Configuration } from '../BackendApiClient/runtime';
import './BotsCnc.css';
import AdvancedScheduleBuilder from './AdvancedScheduleBuilder';

// Type aliases for generated API types
type Task = TaskResponse;
type TaskExecution = TaskExecutionResponse;

// Helper functions
const getTaskName = (task: Task | undefined): string => {
  if (!task || !task.taskType) {
    return 'Unknown Task';
  }

  let name: string = task.taskType.toString();
  if (task.taskParams && Object.keys(task.taskParams).length > 0) {
    const paramKeys = Object.keys(task.taskParams);
    const paramsStr = paramKeys
      .map((key) => `${key}: ${task.taskParams![key]}`)
      .join(', ');
    name += ` (${paramsStr})`;
  }
  return name;
};

const formatDurationWithUnit = (milliseconds: number): string => {
  if (milliseconds >= 3600000) {
    return `${(milliseconds / 3600000).toFixed(1)}h`;
  } else if (milliseconds >= 60000) {
    return `${(milliseconds / 60000).toFixed(1)}m`;
  } else {
    return `${(milliseconds / 1000).toFixed(1)}s`;
  }
};

interface BotColumn {
  bot_id: string;
  tasks: TaskExecution[];
}

// Status colors
const STATUS_COLORS = {
  [TaskStatus.Pending]: '#FFC107', // Yellow
  [TaskStatus.Running]: '#2196F3', // Blue
  [TaskStatus.Finished]: '#4CAF50', // Green
  [TaskStatus.Failed]: '#F44336', // Red
  [TaskStatus.Cancelled]: '#9E9E9E', // Gray
};

const STATUS_LABELS = {
  [TaskStatus.Pending]: 'Pending',
  [TaskStatus.Running]: 'Running',
  [TaskStatus.Finished]: 'Finished',
  [TaskStatus.Failed]: 'Failed',
  [TaskStatus.Cancelled]: 'Cancelled',
};

const BotsCnc: React.FC = () => {
  // API client - use empty basePath to rely on proxy
  const apiConfig = new Configuration({
    basePath: '', // Use relative paths via proxy
  });
  const api = new TasksApi(apiConfig);
  const defaultApi = new DefaultApi(apiConfig);
  const personasApi = new PersonasApi(apiConfig);

  // State
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskExecutions, setTaskExecutions] = useState<TaskExecution[]>([]);
  const [botColumns, setBotColumns] = useState<BotColumn[]>([]);
  const [selectedBotIds, setSelectedBotIds] = useState<string[]>([]);
  const [availablePersonas, setAvailablePersonas] = useState<PersonaResponse[]>(
    [],
  );
  const [availableGroups, setAvailableGroups] = useState<
    PersonaGroupResponse[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [showCancelledTasks, setShowCancelledTasks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [showScheduleGroupModal, setShowScheduleGroupModal] = useState(false);
  const [showKanbanView, setShowKanbanView] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [selectedTaskExecution, setSelectedTaskExecution] = useState<
    any | null
  >(null); // Can be either TaskExecution or ScheduledTask
  const [preSelectedBotId, setPreSelectedBotId] = useState<string | null>(null);
  const [newBotId, setNewBotId] = useState('');
  const [viewMode, setViewMode] = useState<'timeline' | 'kanban'>('timeline'); // Default to timeline view

  // Load data
  const loadData = useCallback(async () => {
    if (loading) return; // Prevent multiple simultaneous requests

    setLoading(true);
    setError(null);
    try {
      const [
        tasksResponse,
        executionsResponse,
        personasResponse,
        groupsResponse,
      ] = await Promise.all([
        api.getTasksApiTasksGet(),
        api.getExecutionsApiTasksExecutionsGet({ timeBackSeconds: 86400 }),
        fetch('/api/personas?include_groups=true').then((res) => res.json()),
        // Fetch groups with users included for the "Add Bot Group to Board" functionality
        fetch('/api/persona-groups?include_users=true').then((res) =>
          res.json(),
        ),
      ]);

      // Handle API response - should now receive full task objects
      if (Array.isArray(tasksResponse)) {
        setTasks(tasksResponse as unknown as Task[]);
      } else {
        setTasks([]);
      }

      const executions = Array.isArray(executionsResponse)
        ? executionsResponse
        : [];
      setTaskExecutions(executions);

      // Filter task executions - hide cancelled, finished, and failed tasks
      const filteredExecutions = executions.filter((execution) => {
        if (showCancelledTasks) {
          return true; // Show all if the toggle is on
        }
        // Only show pending and running tasks
        return (
          execution.taskStatus === TaskStatus.Pending ||
          execution.taskStatus === TaskStatus.Running
        );
      });

      // Group executions by bot_id
      const botGroups = filteredExecutions.reduce(
        (acc, execution) => {
          const botId = execution.userId;
          if (!acc[botId]) {
            acc[botId] = [];
          }
          acc[botId].push(execution);
          return acc;
        },
        {} as Record<string, TaskExecution[]>,
      );

      // Convert to columns, sort tasks by assigned_at
      const columns = Object.entries(botGroups).map(([botId, tasks]) => ({
        bot_id: botId,
        tasks: (tasks as TaskExecution[]).sort(
          (a: TaskExecution, b: TaskExecution) =>
            new Date(a.recordCreationTime).getTime() -
            new Date(b.recordCreationTime).getTime(),
        ),
      }));

      setBotColumns(columns);
      setSelectedBotIds(columns.map((col) => col.bot_id));

      // Load available personas
      const personas = personasResponse as PersonaResponse[];

      // Debug: Check a sample persona
      if (personas.length > 0) {
      }

      // Load available groups
      const groups = groupsResponse as PersonaGroupResponse[];

      // IMPORTANT: Extract personas from groups and merge with main personas list
      // This ensures we have persona data for all bots, even if they're only in groups
      const personasFromGroups: PersonaResponse[] = [];
      groups.forEach((group: any) => {
        if (group.users && Array.isArray(group.users)) {
          group.users.forEach((user: any) => {
            // Convert user object to PersonaResponse format
            // IMPORTANT: Parse region properly from snake_case to camelCase
            const personaFromGroup: PersonaResponse = {
              userId: user.user_id,
              personaType: user.persona_type,
              labels: user.labels,
              demographic: user.demographic,
              groups: user.groups || [],
              region: user.region ? RegionResponseFromJSON(user.region) : null,
              appParameters: user.app_parameters,
              deviceInfo: user.device_info,
            };
            personasFromGroups.push(personaFromGroup);
          });
        }
      });

      // Merge personas, avoiding duplicates (prefer main personas list)
      const personaMap = new Map<string, PersonaResponse>();
      personas.forEach((p) => personaMap.set(p.userId, p));
      personasFromGroups.forEach((p) => {
        if (!personaMap.has(p.userId)) {
          personaMap.set(p.userId, p);
        }
      });

      const allPersonas = Array.from(personaMap.values());

      setAvailablePersonas(allPersonas);

      setAvailableGroups(groups);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      // Set empty data on error
      setTasks([]);
      setTaskExecutions([]);
      setBotColumns([]);
      setSelectedBotIds([]);
      setAvailableGroups([]);
      setAvailablePersonas([]);
    } finally {
      setLoading(false);
    }
  }, [showCancelledTasks]); // Include showCancelledTasks to get updated filtering

  useEffect(() => {
    loadData();
  }, [loadData, showCancelledTasks]);

  // Create new task
  const createTask = async (taskData: InputTask) => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      // Let's also check if the method exists

      // Making the API call

      const response = await api.createTaskApiTasksPost({
        inputTask: taskData,
      });

      await loadData();

      setShowCreateTaskModal(false);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  // Assign task to bots (can be multiple)
  const assignTask = async (
    taskId: number,
    botIds: string[],
    duration: number,
  ) => {
    if (loading) return;
    try {
      // Assign to multiple bots
      const assignments = botIds.map((botId) => ({
        taskId: taskId,
        userId: botId,
        taskStatus: TaskStatus.Pending,
        durationMilliseconds: duration,
      }));

      await Promise.all(
        assignments.map((execution) =>
          api.assignTaskApiTasksAssignPost({ inputTaskExecution: execution }),
        ),
      );

      await loadData();
      setShowAssignTaskModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign task');
    }
  };

  // Change task status
  const changeTaskStatus = async (executionId: number, status: TaskStatus) => {
    if (loading) return;
    try {
      await api.updateStatusApiTasksExecutionsStatusPost({
        updateTaskExecutionRequest: {
          taskExecutionId: executionId,
          taskStatus: status,
          durationMilliseconds: 0,
        },
      });
      await loadData();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to change task status',
      );
    }
  };

  // Schedule group tasks with new block-based structure
  const scheduleGroupTasks = async (groupId: number, schedule: any) => {
    if (loading) return;
    setLoading(true);
    try {
      const endpoint = schedule.use_queue ? '/api/tasks/populate_queue' : '/api/tasks/schedule_group';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId, schedule }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to schedule group tasks');
      }

      const result = await response.json();
      alert(`✅ ${result.message}`);
      await loadData();
      setShowScheduleGroupModal(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to schedule group tasks',
      );
    } finally {
      setLoading(false);
    }
  };

  // Add new bot column
  const addBotColumn = () => {
    if (newBotId && !selectedBotIds.includes(newBotId)) {
      setSelectedBotIds([...selectedBotIds, newBotId]);
      setBotColumns([...botColumns, { bot_id: newBotId, tasks: [] }]);
      setNewBotId('');
    }
  };

  // Add all bots from selected group
  const addGroupBots = () => {
    if (!selectedGroupId) return;

    const selectedGroup = availableGroups.find(
      (group) => (group as any).group_id?.toString() === selectedGroupId,
    );

    if (!selectedGroup) {
      return;
    }

    // Get bot personas from the group that aren't already added
    // Note: selectedGroup.users now contains full user objects, not just IDs
    const groupBotIds = ((selectedGroup as any).users || [])
      .filter((user: any) => {
        // user is now a full persona object, not just an ID

        const personaType = user?.persona_type;
        const isBot = personaType === 'bot';
        const userId = user?.user_id;
        const notAlreadyAdded = userId && !selectedBotIds.includes(userId);

        return user && isBot && notAlreadyAdded;
      })
      .map((user: any) => user.user_id); // Extract user IDs from the filtered users

    if (groupBotIds.length === 0) {
      alert(
        'No new bot personas found in this group or all bots from this group are already on the board.',
      );
      return;
    }

    // Add the new bot columns
    const newBotColumns = groupBotIds.map((botId: string) => ({
      bot_id: botId,
      tasks: [],
    }));
    setSelectedBotIds([...selectedBotIds, ...groupBotIds]);
    setBotColumns([...botColumns, ...newBotColumns]);
    setSelectedGroupId('');
  };

  // Remove bot column
  const removeBotColumn = (botId: string) => {
    setSelectedBotIds(selectedBotIds.filter((id) => id !== botId));
    setBotColumns(botColumns.filter((col) => col.bot_id !== botId));
  };

  // Handle task click (for editing/canceling)
  const handleTaskClick = (task: any) => {
    // Can receive either TaskExecution or ScheduledTask
    setSelectedTaskExecution(task);
    setShowEditTaskModal(true);
  };

  // Handle add task to specific bot from timeline
  const handleAddTaskToBot = (botId: string) => {
    setPreSelectedBotId(botId);
    setShowAssignTaskModal(true);
  };

  // Handle task cancellation
  const handleCancelTask = async (taskExecutionId: number) => {
    if (!window.confirm('Are you sure you want to cancel this task?')) {
      return;
    }
    await changeTaskStatus(taskExecutionId, TaskStatus.Cancelled);
    setShowEditTaskModal(false);
    setSelectedTaskExecution(null);
    await loadData(); // Reload data to refresh the timeline
  };

  // Handle task update
  const handleUpdateTask = async (taskExecutionId: number, updates: any) => {
    try {
      const response = await fetch(`/api/tasks/executions/${taskExecutionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update task');
      }

      await loadData(); // Reload data to refresh the timeline
    } catch (error) {
      throw error; // Re-throw to let the modal handle it
    }
  };

  // Handle clearing all pending tasks
  const handleClearAllPendingTasks = async () => {
    if (
      !window.confirm(
        '⚠️ Are you sure you want to clear ALL pending (future) tasks? This cannot be undone.',
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/tasks/executions/pending', {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to clear tasks');
      }

      const result = await response.json();
      alert(`✅ ${result.message}`);
      await loadData(); // Reload data to refresh the view
    } catch (error) {
      alert(
        'Failed to clear pending tasks: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle clearing pending tasks for a specific bot
  const handleClearBotTasks = async (botId: string) => {
    try {
      setLoading(true);

      // Get pending tasks for this bot
      const botTasks = taskExecutions.filter(
        (te) => te.userId === botId && te.taskStatus === TaskStatus.Pending,
      );

      // Cancel each pending task
      for (const taskExec of botTasks) {
        await changeTaskStatus(taskExec.taskExecutionId, TaskStatus.Cancelled);
      }

      alert(`✅ Cleared ${botTasks.length} pending task(s) for ${botId}`);
      await loadData(); // Reload data
    } catch (error) {
      alert(
        'Failed to clear bot tasks: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && botColumns.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-card glass-card">
          <div className="loading-icon">🚀</div>
          <div className="loading-text">Initializing Command Center...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bots-cnc-container">
      <div className="bots-cnc-bg-pattern"></div>

      <div className="bots-cnc-content">
        <div className="glass-card bots-cnc-header">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon">
                <span>🎮</span>
              </div>
              <div>
                <h1 className="header-title">Bot Command & Control</h1>
                <p className="header-subtitle">
                  Manage your bot personas and task assignments
                </p>
              </div>
            </div>

            <div className="stats-container">
              <div className="stat-item">
                <div className="stat-label">Total Tasks</div>
                <div className="stat-value blue">{tasks.length}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Active Bots</div>
                <div className="stat-value purple">{botColumns.length}</div>
              </div>

              <button
                onClick={() => {
                  setShowCreateTaskModal(true);
                }}
                className="btn-primary"
              >
                ✨ Create Task
              </button>
              <button
                onClick={() => {
                  setPreSelectedBotId(null);
                  setShowAssignTaskModal(true);
                }}
                className="btn-secondary"
              >
                🎯 Assign Task
              </button>
              <button
                onClick={() => setShowScheduleGroupModal(true)}
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                }}
              >
                📅 Schedule Group
              </button>
              <button
                onClick={handleClearAllPendingTasks}
                disabled={loading}
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                🗑️ Clear All Future Tasks
              </button>
              <button
                onClick={() =>
                  setViewMode(viewMode === 'timeline' ? 'kanban' : 'timeline')
                }
                className="btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                }}
              >
                {viewMode === 'timeline'
                  ? '📊 Switch to Kanban'
                  : '📅 Switch to Timeline'}
              </button>
              <button
                onClick={loadData}
                disabled={loading}
                className="btn-primary"
                style={{ opacity: loading ? 0.6 : 1 }}
              >
                {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
              </button>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: '16px',
                }}
              >
                <input
                  type="checkbox"
                  id="showCancelled"
                  checked={showCancelledTasks}
                  onChange={(e) => setShowCancelledTasks(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: '#60a5fa',
                  }}
                />
                <label
                  htmlFor="showCancelled"
                  style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.9)',
                    cursor: 'pointer',
                  }}
                >
                  Show all tasks (including finished/failed)
                </label>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner glass-card">
            <div className="error-content">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="error-close">
              ×
            </button>
          </div>
        )}

        {viewMode === 'timeline' ? (
          <ScheduleViewModal
            botIds={selectedBotIds}
            personas={availablePersonas}
            groups={availableGroups}
            onClose={() => {}} // No close button for main view
            isModal={false} // Render inline, not as modal
            onTaskClick={handleTaskClick}
            onAddTaskToBot={handleAddTaskToBot}
            onClearBotTasks={handleClearBotTasks}
          />
        ) : (
          <>
            <div className="glass-card add-persona-section">
              <div className="add-persona-content">
                <div className="add-persona-label">
                  <div className="add-persona-icon">
                    <span>🤖</span>
                  </div>
                  <label className="add-persona-text">
                    Add Bot Persona to Board:
                  </label>
                </div>
                <select
                  value={newBotId}
                  onChange={(e) => setNewBotId(e.target.value)}
                  className="persona-input"
                  style={{ flex: 1, maxWidth: '300px' }}
                >
                  <option value="">Select a persona...</option>
                  {availablePersonas.map((persona) => (
                    <option key={persona.userId} value={persona.userId}>
                      🤖 {persona.userId} - {persona.personaType}{' '}
                      {persona.labels ? `(${persona.labels})` : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addBotColumn}
                  disabled={!newBotId || selectedBotIds.includes(newBotId)}
                  className="btn-success"
                  style={{
                    opacity:
                      !newBotId || selectedBotIds.includes(newBotId) ? 0.5 : 1,
                  }}
                >
                  ➕ Add to Board
                </button>
              </div>
            </div>

            <div className="glass-card add-persona-section">
              <div className="add-persona-content">
                <div className="add-persona-label">
                  <div className="add-persona-icon">
                    <span>👥</span>
                  </div>
                  <label className="add-persona-text">
                    Add Bot Group to Board:
                  </label>
                </div>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="persona-input"
                  style={{ flex: 1, maxWidth: '300px' }}
                >
                  <option value="">Select a group...</option>
                  {(() => {
                    const filteredGroups = availableGroups.filter(
                      (group) =>
                        (group as any).group_id !== undefined &&
                        (group as any).group_id !== null,
                    );
                    return filteredGroups.map((group) => {
                      const groupId = (group as any).group_id;
                      const userCount = (group as any).user_count;
                      return (
                        <option key={groupId} value={groupId!.toString()}>
                          👥 {group.name} ({userCount} members)
                        </option>
                      );
                    });
                  })()}
                </select>
                <button
                  onClick={addGroupBots}
                  disabled={!selectedGroupId}
                  className="btn-success"
                  style={{ opacity: !selectedGroupId ? 0.5 : 1 }}
                >
                  ➕ Add Group Bots
                </button>
              </div>
            </div>

            <div
              className="kanban-board"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
                alignItems: 'start',
              }}
            >
              {botColumns.length > 0 ? (
                botColumns.map((column) => (
                  <BotColumn
                    key={column.bot_id}
                    column={column}
                    tasks={tasks}
                    personas={availablePersonas}
                    onRemoveColumn={removeBotColumn}
                    onChangeTaskStatus={changeTaskStatus}
                    onAssignTaskToBot={(botId) => {
                      setPreSelectedBotId(botId);
                      setShowAssignTaskModal(true);
                    }}
                    onClearBotTasks={handleClearBotTasks}
                  />
                ))
              ) : (
                <div className="empty-state-large">
                  <div className="glass-card">
                    <div className="empty-icon">🚀</div>
                    <h3 className="empty-title">Ready to Launch</h3>
                    <p className="empty-description">
                      Create your first bot persona to start managing tasks in
                      this command center.
                    </p>
                    {tasks.length === 0 && (
                      <div className="empty-tip">
                        💡 Tip: Start by creating a task, then add bot personas
                        to assign work to them.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {showCreateTaskModal && (
          <CreateTaskModal
            onClose={() => {
              setShowCreateTaskModal(false);
            }}
            onSubmit={createTask}
            loading={loading}
          />
        )}

        {showAssignTaskModal && (
          <AssignTaskModal
            tasks={tasks}
            botIds={selectedBotIds}
            personas={availablePersonas}
            preSelectedBotId={preSelectedBotId}
            onClose={() => {
              setShowAssignTaskModal(false);
              setPreSelectedBotId(null);
            }}
            onSubmit={assignTask}
          />
        )}

        {showScheduleGroupModal && (
          <AdvancedScheduleBuilder
            groups={availableGroups}
            onClose={() => setShowScheduleGroupModal(false)}
            onSubmit={scheduleGroupTasks}
            loading={loading}
          />
        )}

        {showEditTaskModal && selectedTaskExecution && (
          <EditTaskModal
            task={selectedTaskExecution}
            onClose={() => {
              setShowEditTaskModal(false);
              setSelectedTaskExecution(null);
            }}
            onCancel={handleCancelTask}
            onUpdate={handleUpdateTask}
            allTasks={taskExecutions}
          />
        )}
      </div>
    </div>
  );
};

// Bot Column Component
interface BotColumnProps {
  column: BotColumn;
  tasks: Task[];
  personas: PersonaResponse[];
  onRemoveColumn: (botId: string) => void;
  onChangeTaskStatus: (executionId: number, status: TaskStatus) => void;
  onAssignTaskToBot: (botId: string) => void;
  onClearBotTasks?: (botId: string) => void;
}

const BotColumn: React.FC<BotColumnProps> = ({
  column,
  tasks,
  personas,
  onRemoveColumn,
  onChangeTaskStatus,
  onAssignTaskToBot,
  onClearBotTasks,
}) => {
  const tasksMap = tasks.reduce(
    (acc, task) => {
      acc[task.taskId] = task;
      return acc;
    },
    {} as Record<number, Task>,
  );

  const getColumnStats = () => {
    const pending = column.tasks.filter(
      (t) => t.taskStatus === TaskStatus.Pending,
    ).length;
    const running = column.tasks.filter(
      (t) => t.taskStatus === TaskStatus.Running,
    ).length;
    const finished = column.tasks.filter(
      (t) => t.taskStatus === TaskStatus.Finished,
    ).length;
    const failed = column.tasks.filter(
      (t) => t.taskStatus === TaskStatus.Failed,
    ).length;
    return { pending, running, finished, failed, total: column.tasks.length };
  };

  const stats = getColumnStats();
  const persona = personas.find((p) => p.userId === column.bot_id);

  return (
    <div className="bot-column">
      <div className="column-header">
        <div className="column-header-top">
          <div className="column-persona-info">
            <div className="column-persona-icon">🤖</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="column-persona-name">{column.bot_id}</h3>
              <div
                style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.8)',
                  marginTop: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <div style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {persona?.personaType || 'Bot Persona'}
                </div>

                <div
                  style={{
                    background: 'rgba(59,130,246,0.2)',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    alignSelf: 'flex-start',
                  }}
                >
                  🌍{' '}
                  {persona?.region?.carrierRegion || (
                    <span style={{ opacity: 0.5 }}>No region</span>
                  )}
                </div>

                <div
                  style={{
                    background: 'rgba(34,197,94,0.2)',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    alignSelf: 'flex-start',
                  }}
                >
                  🏷️{' '}
                  {persona?.labels || (
                    <span style={{ opacity: 0.5 }}>No labels</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => onRemoveColumn(column.bot_id)}
            className="column-remove-btn"
            title="Remove persona column"
          >
            ✕
          </button>
        </div>

        <div className="task-stats">
          <div className="status-indicators">
            {stats.pending > 0 && (
              <div className="status-indicator">
                <div className="status-dot pending"></div>
                <span style={{ color: '#fbbf24' }}>{stats.pending}</span>
              </div>
            )}
            {stats.running > 0 && (
              <div className="status-indicator">
                <div className="status-dot running"></div>
                <span style={{ color: '#60a5fa' }}>{stats.running}</span>
              </div>
            )}
            {stats.finished > 0 && (
              <div className="status-indicator">
                <div className="status-dot finished"></div>
                <span style={{ color: '#34d399' }}>{stats.finished}</span>
              </div>
            )}
            {stats.failed > 0 && (
              <div className="status-indicator">
                <div className="status-dot failed"></div>
                <span style={{ color: '#f87171' }}>{stats.failed}</span>
              </div>
            )}
          </div>
          <div className="task-count">
            {stats.total} task{stats.total !== 1 ? 's' : ''}
          </div>
        </div>

        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <button
            onClick={() => onAssignTaskToBot(column.bot_id)}
            className="btn-secondary"
            style={{
              width: '100%',
              fontSize: '12px',
              padding: '8px 12px',
            }}
          >
            🎯 Assign Task to This Bot
          </button>

          {onClearBotTasks && stats.pending > 0 && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `⚠️ Clear all ${stats.pending} pending task(s) for ${column.bot_id}?`,
                  )
                ) {
                  onClearBotTasks(column.bot_id);
                }
              }}
              className="btn-primary"
              style={{
                width: '100%',
                fontSize: '11px',
                padding: '6px 10px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              }}
            >
              🗑️ Clear Pending Tasks
            </button>
          )}
        </div>
      </div>

      <div className="tasks-container">
        {column.tasks.map((execution) => {
          const task = tasksMap[execution.taskId];
          return (
            <TaskCard
              key={execution.taskExecutionId}
              execution={execution}
              task={task}
              onChangeStatus={onChangeTaskStatus}
            />
          );
        })}
        {column.tasks.length === 0 && (
          <div className="empty-state">
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>💤</div>
            <p
              style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '14px',
                margin: '0',
              }}
            >
              No tasks assigned
            </p>
            <p
              style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: '12px',
                margin: '4px 0 0 0',
              }}
            >
              Drag tasks here or assign new ones
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Task Card Component
interface TaskCardProps {
  execution: TaskExecution;
  task?: Task;
  onChangeStatus: (executionId: number, status: TaskStatus) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  execution,
  task,
  onChangeStatus,
}) => {
  const statusColor = STATUS_COLORS[execution.taskStatus as TaskStatus];
  const statusLabel = STATUS_LABELS[execution.taskStatus as TaskStatus];

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getTaskTypeIcon = (type: TaskType) => {
    switch (type) {
      case TaskType.Search:
        return '🔍';
      case TaskType.ForYouFeed:
        return '📱';
      case TaskType.Sleep:
        return '😴';
      default:
        return '📋';
    }
  };

  return (
    <div className="task-card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>
            {task ? getTaskTypeIcon(task.taskType as TaskType) : '📋'}
          </span>
          <div>
            <span
              style={{ fontWeight: '600', color: 'white', fontSize: '14px' }}
            >
              {getTaskName(task)}
            </span>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
              Task #{task?.taskId} • Duration:{' '}
              {execution.durationMilliseconds
                ? formatDurationWithUnit(execution.durationMilliseconds)
                : 'N/A'}
            </div>
          </div>
        </div>
        <div
          style={{
            backgroundColor: statusColor,
            color: 'white',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            textAlign: 'center',
            minWidth: '70px',
          }}
        >
          {statusLabel}
        </div>
      </div>

      {task && (
        <div
          style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '12px',
          }}
        >
          {task.taskParams && Object.keys(task.taskParams).length > 0 && (
            <div
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                padding: '8px',
                marginTop: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.5)',
                  marginBottom: '4px',
                }}
              >
                Parameters:
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.8)',
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(task.taskParams, null, 2)}
              </div>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          fontSize: '12px',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: '12px',
        }}
      >
        <div>
          📅 Created: {new Date(execution.recordCreationTime).toLocaleString()}
        </div>
        {execution.startedAt && (
          <div>
            ▶️ Started: {new Date(execution.startedAt).toLocaleString()}
          </div>
        )}
        {execution.finishedAt && (
          <div>
            ✅ Finished: {new Date(execution.finishedAt).toLocaleString()}
          </div>
        )}
      </div>

      {execution.taskStatus === TaskStatus.Pending && (
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() =>
              onChangeStatus(execution.taskExecutionId, TaskStatus.Cancelled)
            }
            style={{
              width: '100%',
              fontSize: '12px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #6b7280, #4b5563)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            ❌ Cancel Task
          </button>
        </div>
      )}
    </div>
  );
};

// Create Task Modal (simplified)
interface CreateTaskModalProps {
  onClose: () => void;
  onSubmit: (task: InputTask) => void;
  loading: boolean;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  onClose,
  onSubmit,
  loading,
}) => {
  const [taskType, setTaskType] = useState<TaskType>('sleep' as TaskType);
  const [params, setParams] = useState('{}');

  // Search-specific fields
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchLanguage, setSearchLanguage] = useState('en');
  const [saveVideos, setSaveVideos] = useState(false);

  const handleSaveVideosToggle = (checked: boolean) => {
    setSaveVideos(checked);
    if (taskType !== 'search') {
      try {
        const parsed = JSON.parse(params);
        if (checked) {
          parsed.save_videos = true;
        } else {
          delete parsed.save_videos;
        }
        setParams(JSON.stringify(parsed, null, 2));
      } catch {}
    }
  };

  const handleParamsChange = (value: string) => {
    setParams(value);
    try {
      const parsed = JSON.parse(value);
      setSaveVideos(!!parsed.save_videos);
    } catch {}
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let taskParams;

      if (taskType === 'search') {
        taskParams = {
          search_keyword: searchKeyword,
          search_keyword_language: searchLanguage,
          ...(saveVideos ? { save_videos: true } : {}),
        };
      } else {
        taskParams = JSON.parse(params);
      }

      const taskData = {
        taskType: taskType,
        taskParams: taskParams,
      };

      onSubmit(taskData);
    } catch (err) {
      console.error('Error parsing JSON:', err);
      alert(
        'Invalid JSON in task parameters: ' +
          (err instanceof Error ? err.message : 'Unknown error'),
      );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="glass-card"
        style={{ width: '100%', maxWidth: '400px', padding: '32px' }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>✨</span>
          Create New Task
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Task Type
            </label>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as TaskType)}
              className="persona-input"
              style={{ width: '100%' }}
            >
              <option value="sleep">😴 Sleep</option>
              <option value="for_you_feed">📱 For You Feed</option>
              <option value="search">🔍 Search</option>
            </select>
          </div>

          {taskType === 'search' ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  marginBottom: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <input
                  type="checkbox"
                  id="saveVideosSearch"
                  checked={saveVideos}
                  onChange={(e) => handleSaveVideosToggle(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label
                  htmlFor="saveVideosSearch"
                  style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.9)',
                    cursor: 'pointer',
                  }}
                >
                  Save videos
                </label>
              </div>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px',
                }}
              >
                Search Parameters
              </label>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.7)',
                      marginBottom: '4px',
                    }}
                  >
                    Search Keyword
                  </label>
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder="e.g., trump, climate change, technology"
                    className="persona-input"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.7)',
                      marginBottom: '4px',
                    }}
                  >
                    Language
                  </label>
                  <select
                    value={searchLanguage}
                    onChange={(e) => setSearchLanguage(e.target.value)}
                    className="persona-input"
                    style={{ width: '100%' }}
                  >
                    <option value="en">🇺🇸 English</option>
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {taskType === 'for_you_feed' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    marginBottom: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <input
                    type="checkbox"
                    id="saveVideos"
                    checked={saveVideos}
                    onChange={(e) => handleSaveVideosToggle(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label
                    htmlFor="saveVideos"
                    style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.9)',
                      cursor: 'pointer',
                    }}
                  >
                    Save videos
                  </label>
                </div>
              )}
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px',
                }}
              >
                Task Parameters (JSON)
              </label>
              <textarea
                value={params}
                onChange={(e) => handleParamsChange(e.target.value)}
                placeholder='{"key": "value"}'
                className="persona-input"
                style={{
                  width: '100%',
                  height: '96px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                }}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #6b7280, #4b5563)',
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '⏳ Creating...' : '✨ Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Task Modal (simplified)
interface AssignTaskModalProps {
  tasks: Task[];
  botIds: string[];
  personas: PersonaResponse[];
  preSelectedBotId?: string | null;
  onClose: () => void;
  onSubmit: (taskId: number, botIds: string[], duration: number) => void;
}

const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
  tasks,
  botIds,
  personas,
  preSelectedBotId,
  onClose,
  onSubmit,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<number>(
    tasks[0]?.taskId || 0,
  );
  const [selectedBotIds, setSelectedBotIds] = useState<string[]>(
    preSelectedBotId ? [preSelectedBotId] : [],
  );
  const [duration, setDuration] = useState(30);
  const [durationUnit, setDurationUnit] = useState<
    'seconds' | 'minutes' | 'hours'
  >('seconds');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTaskId && selectedBotIds.length > 0) {
      // Convert duration to milliseconds
      const durationMs =
        durationUnit === 'hours'
          ? duration * 3600000
          : durationUnit === 'minutes'
            ? duration * 60000
            : duration * 1000;
      onSubmit(selectedTaskId, selectedBotIds, durationMs);
    }
  };

  const toggleBotSelection = (botId: string) => {
    setSelectedBotIds((prev) =>
      prev.includes(botId)
        ? prev.filter((id) => id !== botId)
        : [...prev, botId],
    );
  };

  if (tasks.length === 0) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
        }}
      >
        <div
          className="glass-card"
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '24px',
            }}
          >
            🎯 Assign Task to Bot
          </h2>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>
            No tasks available to assign.
          </p>
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '24px',
            }}
          >
            Create a task first using the "Create Task" button.
          </p>
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (botIds.length === 0) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
        }}
      >
        <div
          className="glass-card"
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '24px',
            }}
          >
            🎯 Assign Task to Bot
          </h2>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>
            No bot personas available.
          </p>
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '24px',
            }}
          >
            Add a bot persona first using the input above.
          </p>
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        className="glass-card"
        style={{ width: '100%', maxWidth: '400px', padding: '32px' }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>🎯</span>
          Assign Task to Bot
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Select Task
            </label>
            <select
              value={selectedTaskId}
              onChange={(e) => setSelectedTaskId(parseInt(e.target.value))}
              className="persona-input"
              style={{ width: '100%' }}
            >
              {tasks
                .filter((task) => task && task.taskType)
                .map((task) => (
                  <option key={task.taskId} value={task.taskId}>
                    #{task.taskId} - {task.taskType.toString()}
                    {task.taskParams &&
                      Object.keys(task.taskParams).length > 0 &&
                      ` (${Object.keys(task.taskParams)
                        .map((k) => `${k}: ${task.taskParams![k]}`)
                        .join(', ')})`}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Duration
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                className="persona-input"
                style={{ flex: '2' }}
                min="1"
              />
              <select
                value={durationUnit}
                onChange={(e) =>
                  setDurationUnit(
                    e.target.value as 'seconds' | 'minutes' | 'hours',
                  )
                }
                className="persona-input"
                style={{ flex: '1' }}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Select Bot Personas ({selectedBotIds.length} selected)
            </label>
            <div
              style={{
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                padding: '8px',
              }}
            >
              {personas
                .filter((p) => botIds.includes(p.userId))
                .map((persona) => (
                  <div
                    key={persona.userId}
                    onClick={() => toggleBotSelection(persona.userId)}
                    style={{
                      padding: '8px 12px',
                      margin: '4px 0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: selectedBotIds.includes(persona.userId)
                        ? 'linear-gradient(135deg, rgba(96,165,250,0.3), rgba(59,130,246,0.3))'
                        : 'rgba(255,255,255,0.05)',
                      border: selectedBotIds.includes(persona.userId)
                        ? '1px solid #60a5fa'
                        : '1px solid transparent',
                      color: 'white',
                      fontSize: '14px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span>
                        {selectedBotIds.includes(persona.userId) ? '✅' : '⚪'}
                      </span>
                      <span>🤖 {persona.userId}</span>
                      <span
                        style={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '12px',
                        }}
                      >
                        {persona.personaType}{' '}
                        {persona.labels ? `(${persona.labels})` : ''}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #6b7280, #4b5563)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-secondary"
              disabled={!selectedTaskId || selectedBotIds.length === 0}
              style={{
                opacity:
                  !selectedTaskId || selectedBotIds.length === 0 ? 0.5 : 1,
              }}
            >
              🎯 Assign to {selectedBotIds.length} Bot
              {selectedBotIds.length !== 1 ? 's' : ''}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Task Modal
interface EditTaskModalProps {
  task: any; // Can be either TaskExecution or ScheduledTask
  onClose: () => void;
  onCancel: (taskExecutionId: number) => void;
  onUpdate?: (taskExecutionId: number, updates: any) => Promise<void>;
  allTasks?: any[]; // All tasks for overlap checking
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  task,
  onClose,
  onCancel,
  onUpdate,
  allTasks = [],
}) => {
  const [taskType, setTaskType] = useState(task.taskType || '');
  const [taskParams, setTaskParams] = useState(task.taskParams || {});
  const [startTime, setStartTime] = useState(
    task.start_time ? new Date(task.start_time).toISOString().slice(0, 16) : '',
  );
  const [endTime, setEndTime] = useState(
    task.end_time ? new Date(task.end_time).toISOString().slice(0, 16) : '',
  );
  const [hasOverlap, setHasOverlap] = useState(false);
  const [saving, setSaving] = useState(false);

  // Calculate duration in minutes from start and end times
  const durationMinutes = React.useMemo(() => {
    if (!startTime || !endTime) return 0;
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    return Math.round((end - start) / 60000);
  }, [startTime, endTime]);

  // Update task params when task type changes
  React.useEffect(() => {
    if (taskType === 'for_you_feed') {
      setTaskParams((prev: any) => {
        const next: any = {};
        if (prev.save_videos) next.save_videos = true;
        return next;
      });
    } else if (taskType === 'sleep') {
      setTaskParams({});
    } else if (taskType === 'search') {
      setTaskParams((prev: any) => {
        if (prev.search_keyword) return prev;
        return {
          search_keyword: '',
          search_keyword_language: 'en',
          ...(prev.save_videos ? { save_videos: true } : {}),
        };
      });
    }
  }, [taskType]);

  const getTaskIcon = (taskType: string) => {
    switch (taskType) {
      case 'for_you_feed':
        return '📱';
      case 'sleep':
        return '😴';
      case 'search':
        return '🔍';
      default:
        return '📋';
    }
  };

  const formatTaskType = (taskType: string) => {
    return taskType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Check for overlapping tasks whenever timing changes
  const checkOverlap = () => {
    if (!startTime || !endTime || !task.user_id) return false;

    const newStart = new Date(startTime).getTime();
    const newEnd = new Date(endTime).getTime();

    const overlappingTasks = allTasks.filter((t) => {
      // Don't check against itself
      if (t.task_execution_id === task.task_execution_id) return false;
      // Only check tasks for the same bot
      if (t.user_id !== task.user_id) return false;

      const tStart = new Date(t.start_time).getTime();
      const tEnd = new Date(t.end_time).getTime();

      // Check if tasks overlap
      return newStart < tEnd && newEnd > tStart;
    });

    return overlappingTasks.length > 0;
  };

  // Update overlap status when timing changes
  React.useEffect(() => {
    setHasOverlap(checkOverlap());
  }, [startTime, endTime]);

  const handleSave = async () => {
    if (!onUpdate) return;

    // Validate search query if task type is search
    if (taskType === 'search' && !taskParams.search_keyword?.trim()) {
      alert('Search query is required for search tasks');
      return;
    }

    // Validate times
    if (!startTime || !endTime) {
      alert('Start time and end time are required');
      return;
    }

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();

    if (end <= start) {
      alert('End time must be after start time');
      return;
    }

    const duration_ms = end - start;

    setSaving(true);
    try {
      const updates = {
        taskType: taskType,
        taskParams: taskParams,
        start_time: new Date(startTime).toISOString(),
        duration_milliseconds: duration_ms,
      };

      await onUpdate(task.task_execution_id, updates);
      onClose();
    } catch (error) {
      alert(
        'Failed to update task: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setSaving(false);
    }
  };

  const durationHours = Math.round((durationMinutes / 60) * 10) / 10;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="glass-card"
        style={{ width: '100%', maxWidth: '500px', padding: '32px' }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{getTaskIcon(task.taskType)}</span>
          Task Details
        </h2>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '4px',
              }}
            >
              Task Type
            </label>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              className="persona-input"
              style={{ width: '100%', fontSize: '14px' }}
              disabled={
                task.task_status === 'running' ||
                task.task_status === 'finished'
              }
            >
              <option value="for_you_feed">📱 For You Feed</option>
              <option value="sleep">😴 Sleep</option>
              <option value="search">🔍 Search</option>
            </select>
          </div>

          {(taskType === 'for_you_feed' || taskType === 'search') && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <input
                type="checkbox"
                id="editSaveVideos"
                checked={!!taskParams.save_videos}
                onChange={(e) => {
                  const next = { ...taskParams };
                  if (e.target.checked) {
                    next.save_videos = true;
                  } else {
                    delete next.save_videos;
                  }
                  setTaskParams(next);
                }}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                disabled={
                  task.task_status === 'running' ||
                  task.task_status === 'finished'
                }
              />
              <label
                htmlFor="editSaveVideos"
                style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                }}
              >
                Save videos
              </label>
            </div>
          )}

          {/* Task Parameters Section */}
          {taskType === 'search' && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '4px',
                }}
              >
                Search Query
              </label>
              <input
                type="text"
                value={taskParams.search_keyword || ''}
                onChange={(e) =>
                  setTaskParams({
                    ...taskParams,
                    search_keyword: e.target.value,
                  })
                }
                className="persona-input"
                style={{ width: '100%', fontSize: '14px' }}
                placeholder="Enter search query..."
                disabled={
                  task.task_status === 'running' ||
                  task.task_status === 'finished'
                }
              />
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '4px',
              }}
            >
              Assigned To
            </label>
            <div
              style={{
                fontSize: '14px',
                color: 'white',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '6px',
              }}
            >
              🤖 {task.user_id}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '4px',
                }}
              >
                Start Time
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="persona-input"
                style={{ width: '100%', fontSize: '14px' }}
                disabled={
                  task.task_status === 'running' ||
                  task.task_status === 'finished'
                }
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '4px',
                }}
              >
                End Time
              </label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="persona-input"
                style={{ width: '100%', fontSize: '14px' }}
                disabled={
                  task.task_status === 'running' ||
                  task.task_status === 'finished'
                }
              />
            </div>
          </div>

          {durationMinutes > 0 && (
            <div
              style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.6)',
                padding: '8px 12px',
                background: 'rgba(96,165,250,0.1)',
                borderRadius: '6px',
              }}
            >
              ⏱️ Duration:{' '}
              <strong>
                {durationMinutes} minutes ({durationHours} hours)
              </strong>
            </div>
          )}

          {hasOverlap && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(239,68,68,0.15)',
                border: '2px solid rgba(239,68,68,0.5)',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#fca5a5',
              }}
            >
              ⚠️ <strong>Warning:</strong> This task overlaps with another task
              for this bot. Tasks may not execute as expected if they overlap.
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '4px',
              }}
            >
              Status
            </label>
            <div
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                padding: '8px 12px',
                background:
                  task.task_status === 'pending'
                    ? 'rgba(251,191,36,0.2)'
                    : task.task_status === 'running'
                      ? 'rgba(59,130,246,0.2)'
                      : 'rgba(107,114,128,0.2)',
                borderRadius: '6px',
                border: `1px solid ${
                  task.task_status === 'pending'
                    ? 'rgba(251,191,36,0.5)'
                    : task.task_status === 'running'
                      ? 'rgba(59,130,246,0.5)'
                      : 'rgba(107,114,128,0.5)'
                }`,
              }}
            >
              {task.task_status.charAt(0).toUpperCase() +
                task.task_status.slice(1)}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <button
            onClick={onClose}
            className="btn-primary"
            style={{ background: 'linear-gradient(135deg, #6b7280, #4b5563)' }}
            disabled={saving}
          >
            Close
          </button>
          {task.task_status === 'pending' && onUpdate && (
            <button
              onClick={handleSave}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
              }}
              disabled={saving || !startTime || !endTime || durationMinutes < 1}
            >
              {saving ? '⏳ Saving...' : '💾 Save Changes'}
            </button>
          )}
          {(task.task_status === 'pending' ||
            task.task_status === 'running') && (
            <button
              onClick={() => {
                onCancel(task.task_execution_id);
              }}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              }}
              disabled={saving}
            >
              ❌ Cancel Task
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Schedule Group Modal
interface ScheduleGroupModalProps {
  groups: PersonaGroupResponse[];
  onClose: () => void;
  onSubmit: (
    groupId: number,
    durationSeconds: number,
    forYouPercentage: number,
    minDurationSeconds: number,
    maxDurationSeconds: number,
    variancePercentage: number,
  ) => void;
  loading: boolean;
}

const ScheduleGroupModal: React.FC<ScheduleGroupModalProps> = ({
  groups,
  onClose,
  onSubmit,
  loading,
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState<'hours' | 'days'>('days');
  const [forYouPercentage, setForYouPercentage] = useState(70);
  const [minDuration, setMinDuration] = useState(5);
  const [maxDuration, setMaxDuration] = useState(30);
  const [variance, setVariance] = useState(20);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroupId) {
      alert('Please select a group');
      return;
    }

    const durationSeconds =
      durationUnit === 'days'
        ? durationValue * 24 * 3600
        : durationValue * 3600;

    onSubmit(
      selectedGroupId,
      durationSeconds,
      forYouPercentage / 100,
      minDuration * 60,
      maxDuration * 60,
      variance / 100,
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="glass-card"
        style={{
          width: '100%',
          maxWidth: '500px',
          padding: '32px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>📅</span>
          Schedule Group Tasks
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Select Group
            </label>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(parseInt(e.target.value))}
              className="persona-input"
              style={{ width: '100%' }}
              required
            >
              <option value={0}>Choose a group...</option>
              {groups.map((group) => {
                const groupData = group as any;
                return (
                  <option key={groupData.group_id} value={groupData.group_id}>
                    👥 {groupData.name} ({groupData.user_count || 0} members)
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Schedule Duration
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="number"
                value={durationValue}
                onChange={(e) =>
                  setDurationValue(parseInt(e.target.value) || 1)
                }
                className="persona-input"
                style={{ flex: '2' }}
                min="1"
                max={durationUnit === 'days' ? 7 : 168}
                required
              />
              <select
                value={durationUnit}
                onChange={(e) =>
                  setDurationUnit(e.target.value as 'hours' | 'days')
                }
                className="persona-input"
                style={{ flex: '1' }}
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginTop: '4px',
              }}
            >
              Max 7 days (168 hours)
            </p>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              For You Feed Percentage: {forYouPercentage}%
            </label>
            <input
              type="range"
              value={forYouPercentage}
              onChange={(e) => setForYouPercentage(parseInt(e.target.value))}
              min="0"
              max="100"
              style={{ width: '100%' }}
            />
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginTop: '4px',
              }}
            >
              Sleep: {100 - forYouPercentage}%
            </p>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Task Duration Range (minutes)
            </label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}
                >
                  Min
                </label>
                <input
                  type="number"
                  value={minDuration}
                  onChange={(e) =>
                    setMinDuration(parseInt(e.target.value) || 1)
                  }
                  className="persona-input"
                  style={{ width: '100%' }}
                  min="1"
                  max={maxDuration}
                  required
                />
              </div>
              <span
                style={{ color: 'rgba(255,255,255,0.5)', paddingTop: '20px' }}
              >
                —
              </span>
              <div style={{ flex: 1 }}>
                <label
                  style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}
                >
                  Max
                </label>
                <input
                  type="number"
                  value={maxDuration}
                  onChange={(e) =>
                    setMaxDuration(parseInt(e.target.value) || 1)
                  }
                  className="persona-input"
                  style={{ width: '100%' }}
                  min={minDuration}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '8px',
              }}
            >
              Bot Behavior Variance: {variance}%
            </label>
            <input
              type="range"
              value={variance}
              onChange={(e) => setVariance(parseInt(e.target.value))}
              min="0"
              max="50"
              style={{ width: '100%' }}
            />
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                marginTop: '4px',
              }}
            >
              How differently each bot behaves (higher = more variation)
            </p>
          </div>

          <div
            style={{
              padding: '16px',
              background: 'rgba(59,130,246,0.1)',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: '8px',
            }}
          >
            <p
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.8)',
                margin: 0,
              }}
            >
              💡 <strong>This will create:</strong> A randomized schedule of For
              You Feed and Sleep tasks for all bots in the group. Each bot will
              have slightly different patterns based on the variance setting.
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #6b7280, #4b5563)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              }}
              disabled={loading || !selectedGroupId}
            >
              {loading ? '⏳ Scheduling...' : '📅 Schedule Tasks'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Schedule View Modal
interface ScheduleViewModalProps {
  botIds: string[];
  personas: PersonaResponse[];
  groups: PersonaGroupResponse[];
  onClose: () => void;
  isModal?: boolean; // If false, renders inline without modal wrapper
  onTaskClick?: (task: any) => void; // Can be either TaskExecution or ScheduledTask
  onAddTaskToBot?: (botId: string) => void;
  onClearBotTasks?: (botId: string) => void;
}

interface ScheduledTask {
  user_id: string;
  task_type: string;
  taskParams?: any;
  start_time: string;
  end_time: string;
  duration_milliseconds: number;
  task_execution_id: number;
  task_status: string;
}

const ScheduleViewModal: React.FC<ScheduleViewModalProps> = ({
  botIds,
  personas,
  groups,
  onClose,
  isModal = true,
  onTaskClick,
  onAddTaskToBot,
  onClearBotTasks,
}) => {
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBotIds, setSelectedBotIds] = useState<string[]>(
    botIds.slice(0, Math.min(10, botIds.length)),
  ); // Show first 10 bots by default
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [viewDays, setViewDays] = useState(1);
  const [viewMode, setViewMode] = useState<'bots-rows' | 'time-rows'>(
    'time-rows',
  ); // Default: bots as columns, time as rows

  useEffect(() => {
    loadSchedule();
  }, [selectedBotIds]);

  const loadSchedule = async () => {
    setLoading(true);
    try {
      const userIdsParam = selectedBotIds.join(',');
      const response = await fetch(
        `/api/tasks/schedule?user_ids=${encodeURIComponent(userIdsParam)}&time_back_seconds=86400`,
      );
      if (!response.ok) throw new Error('Failed to load schedule');
      const data = await response.json();

      // Filter to only show pending and running tasks
      const activeTasks = data.filter(
        (task: ScheduledTask) =>
          task.task_status === 'pending' || task.task_status === 'running',
      );
      setScheduledTasks(activeTasks);
    } catch (err) {
      console.error('Failed to load schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBotSelection = (botId: string) => {
    setSelectedBotIds((prev) =>
      prev.includes(botId)
        ? prev.filter((id) => id !== botId)
        : [...prev, botId],
    );
    setSelectedGroupId(null); // Clear group selection when manually selecting bots
  };

  const handleGroupSelect = (groupId: number | null) => {
    setSelectedGroupId(groupId);
    if (groupId === null) {
      // Show first 5 bots when clearing group
      setSelectedBotIds(botIds.slice(0, 5));
      return;
    }

    const selectedGroup = groups.find((g) => (g as any).group_id === groupId);
    if (!selectedGroup) return;

    // Get all bot personas from this group
    const groupUsers = (selectedGroup as any).users || [];
    const botPersonasInGroup = groupUsers
      .filter((user: any) => user.persona_type === 'bot')
      .map((user: any) => user.user_id);

    setSelectedBotIds(botPersonasInGroup);
  };

  // Calculate timeline
  const getTimeline = () => {
    if (scheduledTasks.length === 0) {
      // Start from the beginning of the current hour
      const now = new Date();
      now.setMinutes(0, 0, 0);
      return { startTime: now, endTime: now, duration: 0 };
    }

    const times = scheduledTasks.map((t) => new Date(t.start_time).getTime());
    const endTimes = scheduledTasks.map((t) => new Date(t.end_time).getTime());

    // Round start time to the beginning of the hour
    const firstTaskTime = new Date(Math.min(...times));
    const startTime = new Date(firstTaskTime);
    startTime.setMinutes(0, 0, 0);

    // Round end time to the end of the hour
    const lastTaskTime = new Date(Math.max(...endTimes));
    const endTime = new Date(lastTaskTime);
    if (endTime.getMinutes() > 0 || endTime.getSeconds() > 0) {
      endTime.setHours(endTime.getHours() + 1);
    }
    endTime.setMinutes(0, 0, 0);

    const duration = endTime.getTime() - startTime.getTime();

    return { startTime, endTime, duration };
  };

  const timeline = getTimeline();
  const hoursToShow = Math.min(
    viewDays * 24,
    Math.ceil(timeline.duration / (1000 * 3600)),
  );

  const content = (
    <div
      className="glass-card"
      style={{
        width: '100%',
        maxWidth: isModal ? '1200px' : '100%',
        height: isModal ? '90vh' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: '32px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>📊</span>
          Bot Schedule Timeline
        </h2>
        {isModal && (
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
            }}
          >
            ×
          </button>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
            Group:
          </label>
          <select
            value={selectedGroupId || ''}
            onChange={(e) =>
              handleGroupSelect(
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="persona-input"
            style={{ width: '200px' }}
          >
            <option value="">All / Custom Selection</option>
            {groups.map((group) => {
              const groupData = group as any;
              const botCount =
                groupData.users?.filter((u: any) => u.persona_type === 'bot')
                  .length || 0;
              return (
                <option key={groupData.group_id} value={groupData.group_id}>
                  👥 {groupData.name} ({botCount} bots)
                </option>
              );
            })}
          </select>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginLeft: '16px',
          }}
        >
          <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
            Duration:
          </label>
          <select
            value={viewDays}
            onChange={(e) => setViewDays(parseInt(e.target.value))}
            className="persona-input"
            style={{ width: 'auto' }}
          >
            <option value={1}>1 Day</option>
            <option value={2}>2 Days</option>
            <option value={3}>3 Days</option>
            <option value={7}>1 Week</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
          <button
            onClick={() => setViewMode('bots-rows')}
            className={
              viewMode === 'bots-rows' ? 'btn-primary' : 'btn-secondary'
            }
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            🤖 Bots as Rows
          </button>
          <button
            onClick={() => setViewMode('time-rows')}
            className={
              viewMode === 'time-rows' ? 'btn-primary' : 'btn-secondary'
            }
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            ⏰ Time as Rows
          </button>
        </div>

        <button
          onClick={loadSchedule}
          className="btn-secondary"
          style={{ marginLeft: 'auto' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Bot Selection - Only show when no group is selected */}
      {!selectedGroupId && (
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '8px',
            }}
          >
            Select Bots to Display ({selectedBotIds.length} selected)
          </label>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              maxHeight: '80px',
              overflow: 'auto',
              padding: '8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
            }}
          >
            {botIds.map((botId) => (
              <button
                key={botId}
                onClick={() => toggleBotSelection(botId)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  background: selectedBotIds.includes(botId)
                    ? 'linear-gradient(135deg, rgba(96,165,250,0.5), rgba(59,130,246,0.5))'
                    : 'rgba(255,255,255,0.1)',
                  color: 'white',
                }}
              >
                {selectedBotIds.includes(botId) ? '✅' : '⚪'} {botId}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Show group info when a group is selected */}
      {selectedGroupId && (
        <div
          style={{
            marginBottom: '20px',
            padding: '12px 16px',
            background: 'rgba(96,165,250,0.1)',
            border: '2px solid rgba(96,165,250,0.3)',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '4px',
            }}
          >
            📊 Viewing schedule for group:{' '}
            <strong>
              {
                groups.find((g) => (g as any).group_id === selectedGroupId)
                  ?.name
              }
            </strong>
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            {selectedBotIds.length} bot{selectedBotIds.length !== 1 ? 's' : ''}{' '}
            in this group
          </div>
        </div>
      )}

      {loading ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
            🔄 Loading schedule...
          </div>
        </div>
      ) : scheduledTasks.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
            No scheduled tasks found for selected bots.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
            Use "Schedule Group" to create tasks.
          </p>
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {viewMode === 'bots-rows' ? (
            <TimelineView
              scheduledTasks={scheduledTasks}
              personas={personas}
              selectedBotIds={selectedBotIds}
              hoursToShow={hoursToShow}
              startTime={timeline.startTime}
              onTaskClick={onTaskClick}
              onAddTaskToBot={onAddTaskToBot}
              onClearBotTasks={onClearBotTasks}
            />
          ) : (
            <TimelineViewTransposed
              scheduledTasks={scheduledTasks}
              personas={personas}
              selectedBotIds={selectedBotIds}
              hoursToShow={hoursToShow}
              startTime={timeline.startTime}
              onTaskClick={onTaskClick}
              onAddTaskToBot={onAddTaskToBot}
              onClearBotTasks={onClearBotTasks}
            />
          )}
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
        }}
      >
        {content}
      </div>
    );
  }

  return content;
};

// Timeline View Component
interface TimelineViewProps {
  scheduledTasks: ScheduledTask[];
  personas: PersonaResponse[];
  selectedBotIds: string[];
  hoursToShow: number;
  startTime: Date;
  onTaskClick?: (task: any) => void; // Can be either TaskExecution or ScheduledTask
  onAddTaskToBot?: (botId: string) => void;
  onClearBotTasks?: (botId: string) => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  scheduledTasks,
  personas,
  selectedBotIds,
  hoursToShow,
  startTime,
  onTaskClick,
  onAddTaskToBot,
  onClearBotTasks,
}) => {
  const HOUR_WIDTH = 100; // pixels per hour
  const ROW_HEIGHT = 100; // Increased for better readability with more info
  const LABEL_WIDTH = 220; // Increased to fit more info

  const getTaskColor = (taskType: string, taskStatus?: string) => {
    // Running tasks get bright blue to stand out
    if (taskStatus === 'running') {
      return 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Bright blue for running tasks
    }
    // All pending tasks are grey regardless of type
    return 'linear-gradient(135deg, #6b7280, #4b5563)'; // Grey for all pending tasks
  };

  const getTaskIcon = (taskType: string) => {
    switch (taskType) {
      case 'for_you_feed':
        return '📱';
      case 'sleep':
        return '😴';
      case 'search':
        return '🔍';
      default:
        return '📋';
    }
  };

  const getTaskLabel = (taskType: string) => {
    switch (taskType) {
      case 'for_you_feed':
        return 'For You';
      case 'sleep':
        return 'Sleep';
      case 'search':
        return 'Search';
      default:
        return taskType;
    }
  };

  // Group tasks by day
  const getDayStart = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const dayStart = getDayStart(startTime);
  const totalDays = Math.ceil(hoursToShow / 24);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: selectedBotIds.length * ROW_HEIGHT + 100,
      }}
    >
      {/* Day headers */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 11,
          background: 'rgba(17,24,39,0.98)',
          borderBottom: '2px solid rgba(96,165,250,0.3)',
          paddingBottom: '8px',
        }}
      >
        <div style={{ display: 'flex', marginLeft: LABEL_WIDTH }}>
          {Array.from({ length: totalDays }).map((_, dayIndex) => {
            const day = new Date(dayStart.getTime() + dayIndex * 86400000);
            return (
              <div
                key={dayIndex}
                style={{
                  width: `${24 * HOUR_WIDTH}px`,
                  textAlign: 'center',
                  padding: '12px 0',
                  borderLeft:
                    dayIndex > 0 ? '2px solid rgba(96,165,250,0.2)' : 'none',
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    marginBottom: '4px',
                  }}
                >
                  {day.toLocaleDateString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Time axis with hour markers */}
      <div
        style={{
          position: 'sticky',
          top: '60px',
          zIndex: 10,
          background: 'rgba(17,24,39,0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          height: '60px',
          display: 'flex',
          marginLeft: LABEL_WIDTH,
        }}
      >
        {Array.from({ length: hoursToShow + 1 }).map((_, i) => {
          const time = new Date(startTime.getTime() + i * 3600000);
          const hour = time.getHours();
          const isNewDay = hour === 0 && i > 0;

          return (
            <div
              key={i}
              style={{
                width: HOUR_WIDTH,
                borderLeft: isNewDay
                  ? '2px solid rgba(96,165,250,0.3)'
                  : '1px solid rgba(255,255,255,0.1)',
                padding: '8px 4px',
                fontSize: '10px',
                color:
                  hour === 0 || hour === 12
                    ? 'rgba(255,255,255,0.9)'
                    : 'rgba(255,255,255,0.6)',
                fontWeight: hour === 0 || hour === 12 ? '600' : 'normal',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <div style={{ fontWeight: '600', fontSize: '11px' }}>
                {time.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              {(hour === 0 || i === 0) && (
                <div
                  style={{
                    fontSize: '9px',
                    color: 'rgba(96,165,250,0.9)',
                    fontWeight: '500',
                  }}
                >
                  {time.toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bot rows */}
      <div style={{ marginTop: '10px' }}>
        {selectedBotIds.map((botId, rowIndex) => {
          const botTasks = scheduledTasks.filter((t) => t.user_id === botId);
          const persona = personas.find((p) => p.userId === botId);

          return (
            <div
              key={botId}
              style={{
                position: 'relative',
                height: ROW_HEIGHT,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                marginBottom: '8px',
                background:
                  rowIndex % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
              }}
            >
              {/* Bot label */}
              <div
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 5,
                  width: LABEL_WIDTH,
                  height: ROW_HEIGHT,
                  background:
                    rowIndex % 2 === 0
                      ? 'rgba(17,24,39,0.98)'
                      : 'rgba(17,24,39,0.95)',
                  borderRight: '2px solid rgba(96,165,250,0.2)',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '20px',
                      background: 'rgba(96,165,250,0.1)',
                      borderRadius: '8px',
                      padding: '6px',
                      border: '1px solid rgba(96,165,250,0.3)',
                    }}
                  >
                    🤖
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'white',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: '3px',
                      }}
                    >
                      {botId.length > 18
                        ? botId.substring(0, 16) + '...'
                        : botId}
                    </div>
                    {persona?.region?.carrierRegion && (
                      <div
                        style={{
                          fontSize: '9px',
                          color: 'rgba(255,255,255,0.7)',
                          background: 'rgba(59,130,246,0.15)',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          display: 'inline-block',
                          marginBottom: '2px',
                        }}
                      >
                        🌍 {persona.region.carrierRegion}
                      </div>
                    )}
                    {persona?.groups && persona.groups.length > 0 && (
                      <div
                        style={{
                          fontSize: '8px',
                          color: 'rgba(255,255,255,0.65)',
                          background: 'rgba(139,92,246,0.15)',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          display: 'inline-block',
                          marginBottom: '2px',
                          marginLeft: '3px',
                        }}
                      >
                        👥 {persona.groups.join(', ').substring(0, 12)}
                      </div>
                    )}
                    {persona?.labels && (
                      <div
                        style={{
                          fontSize: '8px',
                          color: 'rgba(255,255,255,0.65)',
                          background: 'rgba(34,197,94,0.15)',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          display: 'inline-block',
                        }}
                      >
                        🏷️ {persona.labels.substring(0, 12)}
                      </div>
                    )}
                  </div>
                </div>
                {onAddTaskToBot && (
                  <button
                    onClick={() => onAddTaskToBot(botId)}
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(96,165,250,0.3), rgba(59,130,246,0.3))',
                      border: '1px solid rgba(96,165,250,0.5)',
                      borderRadius: '4px',
                      padding: '4px 6px',
                      fontSize: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(96,165,250,0.5), rgba(59,130,246,0.5))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(96,165,250,0.3), rgba(59,130,246,0.3))';
                    }}
                  >
                    ➕ Add Task
                  </button>
                )}
              </div>

              {/* Tasks timeline */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: LABEL_WIDTH,
                  right: 0,
                  height: ROW_HEIGHT,
                }}
              >
                {/* Day separators */}
                {Array.from({ length: totalDays }).map((_, dayIndex) => (
                  <div
                    key={dayIndex}
                    style={{
                      position: 'absolute',
                      left: `${dayIndex * 24 * HOUR_WIDTH}px`,
                      width: `${24 * HOUR_WIDTH}px`,
                      height: ROW_HEIGHT,
                      borderLeft:
                        dayIndex > 0
                          ? '2px solid rgba(96,165,250,0.1)'
                          : 'none',
                      pointerEvents: 'none',
                    }}
                  />
                ))}

                {/* Tasks */}
                {botTasks.map((task, taskIndex) => {
                  const taskStart = new Date(task.start_time).getTime();
                  const taskEnd = new Date(task.end_time).getTime();
                  const offsetMs = taskStart - startTime.getTime();
                  const durationMs = taskEnd - taskStart;

                  const left = (offsetMs / 3600000) * HOUR_WIDTH;
                  const width = Math.max(
                    (durationMs / 3600000) * HOUR_WIDTH,
                    40,
                  ); // Min width for readability

                  const durationMinutes = Math.round(durationMs / 60000);
                  const showDetails = width > 60; // Only show details if there's enough space

                  return (
                    <div
                      key={task.task_execution_id}
                      onClick={() => onTaskClick && onTaskClick(task)}
                      style={{
                        position: 'absolute',
                        left: `${left}px`,
                        width: `${width}px`,
                        top: '8px',
                        height: ROW_HEIGHT - 16,
                        background: getTaskColor(
                          task.task_type,
                          task.task_status,
                        ),
                        borderRadius: '6px',
                        padding: '0',
                        fontSize: '12px',
                        color: 'white',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border:
                          task.task_status === 'running'
                            ? '2px solid #3b82f6'
                            : 'none',
                        boxShadow:
                          task.task_status === 'running'
                            ? '0 0 20px rgba(59,130,246,0.6), 0 2px 8px rgba(0,0,0,0.4)'
                            : '0 2px 8px rgba(0,0,0,0.4)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        animation:
                          task.task_status === 'running'
                            ? 'pulse 2s ease-in-out infinite'
                            : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '0',
                      }}
                      title={`${getTaskIcon(task.task_type)} ${getTaskLabel(task.task_type)}\n${new Date(task.start_time).toLocaleString()} - ${new Date(task.end_time).toLocaleTimeString()}\nDuration: ${durationMinutes} min\nStatus: ${task.task_status}\nClick to edit/cancel`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform =
                          'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.boxShadow =
                          '0 4px 12px rgba(0,0,0,0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform =
                          'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow =
                          '0 2px 8px rgba(0,0,0,0.4)';
                      }}
                    >
                      <div
                        style={{
                          padding: '8px 10px',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '2px',
                          background: 'transparent',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          }}
                        >
                          <span>{getTaskIcon(task.task_type)}</span>
                          {showDetails && (
                            <span>{getTaskLabel(task.task_type)}</span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '10px',
                            opacity: 0.9,
                            fontWeight: '500',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          }}
                        >
                          {durationMinutes}m
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Transposed Timeline View (Time as rows, Bots as columns)
const TimelineViewTransposed: React.FC<TimelineViewProps> = ({
  scheduledTasks,
  personas,
  selectedBotIds,
  hoursToShow,
  startTime,
  onTaskClick,
  onAddTaskToBot,
  onClearBotTasks,
}) => {
  const COLUMN_WIDTH = 200; // Width per bot column (increased for better readability and add task button)
  const HOUR_HEIGHT = 70; // Height per hour row (increased for better spacing)
  const HEADER_HEIGHT = 200; // Increased to fit all info and buttons

  const getTaskColor = (taskType: string, taskStatus?: string) => {
    // Running tasks get bright blue to stand out
    if (taskStatus === 'running') {
      return 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Bright blue for running tasks
    }
    // All pending tasks are grey regardless of type
    return 'linear-gradient(135deg, #6b7280, #4b5563)'; // Grey for all pending tasks
  };

  const getTaskIcon = (taskType: string) => {
    switch (taskType) {
      case 'for_you_feed':
        return '📱';
      case 'sleep':
        return '😴';
      case 'search':
        return '🔍';
      default:
        return '📋';
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        minWidth: selectedBotIds.length * COLUMN_WIDTH + 100,
      }}
    >
      {/* Header with bot columns */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background:
            'linear-gradient(180deg, rgba(17,24,39,1) 0%, rgba(17,24,39,0.98) 100%)',
          borderBottom: '3px solid rgba(96,165,250,0.4)',
          height: HEADER_HEIGHT,
          display: 'flex',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}
      >
        {/* Time column header */}
        <div
          style={{
            width: '120px',
            borderRight: '3px solid rgba(96,165,250,0.4)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(96,165,250,0.05)',
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏰</div>
          <div style={{ fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
            Time
          </div>
        </div>

        {/* Bot column headers */}
        {selectedBotIds.map((botId, index) => {
          const persona = personas.find((p) => p.userId === botId);
          const pendingTasksForBot = scheduledTasks.filter(
            (t) => t.user_id === botId && t.task_status === 'pending',
          );

          return (
            <div
              key={botId}
              style={{
                width: COLUMN_WIDTH,
                borderRight: '2px solid rgba(255,255,255,0.1)',
                padding: '8px 6px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                background:
                  index % 2 === 0
                    ? 'rgba(96,165,250,0.05)'
                    : 'rgba(96,165,250,0.02)',
                transition: 'background 0.2s',
              }}
            >
              <div
                style={{
                  fontSize: '24px',
                  background: 'rgba(96,165,250,0.15)',
                  borderRadius: '10px',
                  padding: '6px 10px',
                  border: '2px solid rgba(96,165,250,0.3)',
                }}
              >
                🤖
              </div>
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: 'white',
                  textAlign: 'center',
                  width: '100%',
                  letterSpacing: '0.3px',
                  marginBottom: '2px',
                  wordBreak: 'break-word',
                  lineHeight: '1.2',
                }}
              >
                {botId.length > 20 ? botId.substring(0, 18) + '...' : botId}
              </div>

              <div
                style={{
                  fontSize: '9px',
                  color: 'white',
                  textAlign: 'center',
                  background: 'rgba(59,130,246,0.3)',
                  padding: '3px 4px',
                  borderRadius: '4px',
                  width: '100%',
                  fontWeight: '600',
                  border: '1px solid rgba(59,130,246,0.5)',
                  minHeight: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  wordBreak: 'break-all',
                  lineHeight: '1.2',
                  overflow: 'hidden',
                  maxHeight: '32px',
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  🌍{' '}
                  {persona?.region?.carrierRegion || (
                    <span style={{ opacity: 0.6, fontStyle: 'italic' }}>
                      No region
                    </span>
                  )}
                </span>
              </div>

              <div
                style={{
                  fontSize: '9px',
                  color: 'white',
                  textAlign: 'center',
                  background: 'rgba(34,197,94,0.3)',
                  padding: '3px 4px',
                  borderRadius: '4px',
                  width: '100%',
                  fontWeight: '600',
                  border: '1px solid rgba(34,197,94,0.5)',
                  minHeight: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  wordBreak: 'break-all',
                  lineHeight: '1.2',
                  overflow: 'hidden',
                  maxHeight: '32px',
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  🏷️{' '}
                  {persona?.labels !== null && persona?.labels !== undefined ? (
                    persona.labels || (
                      <span style={{ opacity: 0.6, fontStyle: 'italic' }}>
                        Empty
                      </span>
                    )
                  ) : (
                    <span style={{ opacity: 0.6, fontStyle: 'italic' }}>
                      No labels
                    </span>
                  )}
                </span>
              </div>

              {onAddTaskToBot && (
                <button
                  onClick={() => onAddTaskToBot(botId)}
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(96,165,250,0.3), rgba(59,130,246,0.3))',
                    border: '1px solid rgba(96,165,250,0.5)',
                    borderRadius: '5px',
                    padding: '4px 6px',
                    fontSize: '9px',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '700',
                    transition: 'all 0.2s',
                    marginTop: '4px',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      'linear-gradient(135deg, rgba(96,165,250,0.6), rgba(59,130,246,0.6))';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      'linear-gradient(135deg, rgba(96,165,250,0.3), rgba(59,130,246,0.3))';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ➕ Add
                </button>
              )}

              {onClearBotTasks && (
                <button
                  onClick={() => {
                    const pendingCount = pendingTasksForBot.length;
                    if (pendingCount === 0) {
                      alert('No pending tasks to clear for this bot.');
                      return;
                    }
                    if (
                      window.confirm(
                        `⚠️ Clear ${pendingCount} pending task(s) for ${botId}?`,
                      )
                    ) {
                      onClearBotTasks(botId);
                    }
                  }}
                  disabled={pendingTasksForBot.length === 0}
                  style={{
                    background:
                      pendingTasksForBot.length > 0
                        ? 'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(220,38,38,0.4))'
                        : 'rgba(100,100,100,0.3)',
                    border: '1px solid rgba(239,68,68,0.5)',
                    borderRadius: '5px',
                    padding: '5px 8px',
                    fontSize: '9px',
                    color: 'white',
                    cursor:
                      pendingTasksForBot.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '700',
                    transition: 'all 0.2s',
                    marginTop: '4px',
                    width: '100%',
                    opacity: pendingTasksForBot.length > 0 ? 1 : 0.5,
                  }}
                  onMouseEnter={(e) => {
                    if (pendingTasksForBot.length > 0) {
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(239,68,68,0.7), rgba(220,38,38,0.7))';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (pendingTasksForBot.length > 0) {
                      e.currentTarget.style.background =
                        'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(220,38,38,0.4))';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  🗑️ Clear ({pendingTasksForBot.length})
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Time rows */}
      {Array.from({ length: hoursToShow }).map((_, hourIndex) => {
        const hourTime = new Date(startTime.getTime() + hourIndex * 3600000);
        const hour = hourTime.getHours();
        const isNewDay = hour === 0;
        const isMidnight = hour === 0;
        const isNoon = hour === 12;
        const isKeyHour = isMidnight || isNoon || hour % 6 === 0;

        return (
          <div
            key={hourIndex}
            style={{
              display: 'flex',
              minHeight: HOUR_HEIGHT,
              borderBottom: isNewDay
                ? '3px solid rgba(96,165,250,0.3)'
                : '1px solid rgba(255,255,255,0.06)',
              background: isNewDay
                ? 'rgba(96,165,250,0.05)'
                : hourIndex % 2 === 0
                  ? 'rgba(255,255,255,0.01)'
                  : 'transparent',
              transition: 'background 0.2s',
            }}
          >
            {/* Time label */}
            <div
              style={{
                width: '120px',
                borderRight: '3px solid rgba(96,165,250,0.3)',
                padding: '12px 8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                background: isKeyHour
                  ? 'linear-gradient(90deg, rgba(96,165,250,0.1) 0%, rgba(96,165,250,0.05) 100%)'
                  : 'rgba(17,24,39,0.95)',
                fontWeight: isKeyHour ? 'bold' : 'normal',
              }}
            >
              <div
                style={{
                  fontSize: isKeyHour ? '15px' : '14px',
                  color: isKeyHour ? 'rgba(96,165,250,1)' : 'white',
                  fontWeight: isKeyHour ? '700' : '600',
                  letterSpacing: '0.5px',
                }}
              >
                {hourTime.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              <div
                style={{
                  fontSize: '8px',
                  color: 'rgba(255,255,255,0.4)',
                  marginTop: '2px',
                }}
              >
                to{' '}
                {new Date(hourTime.getTime() + 3600000).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              {isNewDay && (
                <div
                  style={{
                    fontSize: '11px',
                    color: 'rgba(96,165,250,0.9)',
                    marginTop: '4px',
                    fontWeight: '600',
                    background: 'rgba(96,165,250,0.1)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                  }}
                >
                  {hourTime.toLocaleDateString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>

            {/* Bot columns for this hour */}
            {selectedBotIds.map((botId, colIndex) => {
              // Find tasks for this bot in this hour
              const hourStart = hourTime.getTime();
              const hourEnd = hourStart + 3600000;

              const tasksInHour = scheduledTasks.filter((task) => {
                if (task.user_id !== botId) return false;
                const taskStart = new Date(task.start_time).getTime();
                const taskEnd = new Date(task.end_time).getTime();
                // Task overlaps with this hour
                return taskStart < hourEnd && taskEnd > hourStart;
              });

              return (
                <div
                  key={`${botId}-${hourIndex}`}
                  style={{
                    position: 'relative',
                    width: COLUMN_WIDTH,
                    minHeight: HOUR_HEIGHT,
                    borderRight: '2px solid rgba(255,255,255,0.08)',
                    background:
                      colIndex % 2 === 0
                        ? 'rgba(96,165,250,0.03)'
                        : 'rgba(96,165,250,0.01)',
                    padding: '6px',
                  }}
                >
                  {tasksInHour.map((task) => {
                    const taskStart = new Date(task.start_time).getTime();
                    const taskEnd = new Date(task.end_time).getTime();

                    // Calculate position within the hour
                    const relativeStart = Math.max(taskStart, hourStart);
                    const relativeEnd = Math.min(taskEnd, hourEnd);
                    const offsetMs = relativeStart - hourStart;
                    const durationMs = relativeEnd - relativeStart;

                    const top = (offsetMs / 3600000) * HOUR_HEIGHT;
                    const height = Math.max(
                      (durationMs / 3600000) * HOUR_HEIGHT,
                      20,
                    );

                    const totalDuration = taskEnd - taskStart;
                    const durationMinutes = Math.round(totalDuration / 60000);

                    return (
                      <div
                        key={task.task_execution_id}
                        onClick={() => onTaskClick && onTaskClick(task)}
                        style={{
                          position: 'absolute',
                          top: `${top}px`,
                          left: '4px',
                          right: '4px',
                          height: `${height}px`,
                          background: getTaskColor(
                            task.task_type,
                            task.task_status,
                          ),
                          borderRadius: '6px',
                          padding: '0',
                          fontSize: '11px',
                          color: 'white',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border:
                            task.task_status === 'running'
                              ? '2px solid #60a5fa'
                              : 'none',
                          boxShadow:
                            task.task_status === 'running'
                              ? '0 0 20px rgba(96,165,250,0.6), 0 2px 8px rgba(0,0,0,0.4)'
                              : '0 2px 8px rgba(0,0,0,0.4)',
                          animation:
                            task.task_status === 'running'
                              ? 'pulse 2s ease-in-out infinite'
                              : 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          zIndex: 1,
                          backdropFilter: 'blur(4px)',
                        }}
                        title={`${getTaskIcon(task.task_type)} ${task.task_type}\n${new Date(task.start_time).toLocaleTimeString()} - ${new Date(task.end_time).toLocaleTimeString()}\nDuration: ${durationMinutes} min\nClick to edit/cancel`}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            'scale(1.08) translateY(-2px)';
                          e.currentTarget.style.boxShadow =
                            '0 6px 16px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)';
                          e.currentTarget.style.zIndex = '3';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            'scale(1) translateY(0)';
                          e.currentTarget.style.boxShadow =
                            '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)';
                          e.currentTarget.style.zIndex = '1';
                        }}
                      >
                        <div
                          style={{
                            padding: '6px 8px',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'transparent',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: '700',
                              fontSize: '16px',
                              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            }}
                          >
                            {getTaskIcon(task.task_type)}
                          </div>
                          {height > 35 && (
                            <>
                              <div
                                style={{
                                  fontSize: '9px',
                                  opacity: 0.95,
                                  fontWeight: '700',
                                  marginTop: '2px',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                }}
                              >
                                {new Date(task.start_time).toLocaleTimeString(
                                  [],
                                  {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                  },
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: '9px',
                                  opacity: 0.95,
                                  fontWeight: '600',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                }}
                              >
                                {durationMinutes}m
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default BotsCnc;
